import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, Search, X } from "lucide-react";
import { LogEntry, LogFilters, LogRow, ocpiApi } from "../../../api/maintenance/ocpi";
import { CopyBtn, errText, fmtTs } from "./ocpiShared";

const MODULES = ["versions", "credentials", "locations", "tariffs", "sessions", "cdrs", "tokens", "commands", "hubclientinfo"];
const PRESETS: { key: string; label: string; hours: number }[] = [
  { key: "1h", label: "1h", hours: 1 }, { key: "24h", label: "24h", hours: 24 }, { key: "7d", label: "7d", hours: 168 },
  { key: "30d", label: "30d", hours: 720 },
];

function statusClass(http: number | null, ocpi: number | null, limited: boolean): string {
  if (limited) return "warn";
  if ((http ?? 0) >= 500 || (ocpi ?? 0) >= 3000) return "bad";
  if ((http ?? 0) >= 400 || (ocpi ?? 0) >= 2000) return "warn";
  return "ok";
}

function pretty(text: string | null): string {
  if (!text) return "(empty)";
  try { return JSON.stringify(JSON.parse(text), null, 2); } catch { return text; }
}

const OcpiLogViewer: React.FC<{ partnerId?: number }> = ({ partnerId }) => {
  const [filters, setFilters] = useState<LogFilters>({ direction: "", module: "", outcome: "", q: "" });
  const [preset, setPreset] = useState("24h");
  const [rows, setRows] = useState<LogRow[]>([]);
  const [next, setNext] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState<LogEntry | null>(null);
  const [query, setQuery] = useState("");

  const window_ = useCallback(() => {
    const hours = PRESETS.find((p) => p.key === preset)?.hours ?? 24;
    return { start: new Date(Date.now() - hours * 3600 * 1000).toISOString(), end: new Date().toISOString() };
  }, [preset]);

  const load = useCallback(async (append = false, before: number | null = null) => {
    setLoading(true);
    setError("");
    try {
      const res = await ocpiApi.logs({ ...filters, ...window_(), partner_id: partnerId, before_id: before, limit: 100 });
      setRows((prev) => (append ? [...prev, ...res.data.items] : res.data.items));
      setNext(res.data.next_before_id);
    } catch (e: any) {
      setError(errText(e, "Failed to load request log"));
    } finally {
      setLoading(false);
    }
  }, [filters, partnerId, window_]);

  useEffect(() => { load(false, null); }, [load]);

  const openRow = async (id: number) => {
    try {
      setOpen((await ocpiApi.log(id)).data);
    } catch (e: any) {
      setError(errText(e, "Failed to load entry"));
    }
  };

  const set = (patch: Partial<LogFilters>) => setFilters((f) => ({ ...f, ...patch }));

  return (
    <div>
      <div className="ma-toolbar">
        <div className="ma-presets" role="group" aria-label="Time window">
          {PRESETS.map((p) => (
            <button key={p.key} className={`ma-preset ${preset === p.key ? "on" : ""}`} onClick={() => setPreset(p.key)}>{p.label}</button>
          ))}
        </div>
        <label className="ma-select">Direction
          <select value={filters.direction} onChange={(e) => set({ direction: e.target.value as any })}>
            <option value="">Both</option><option value="IN">Inbound (partner → us)</option><option value="OUT">Outbound (us → partner)</option>
          </select>
        </label>
        <label className="ma-select">Module
          <select value={filters.module} onChange={(e) => set({ module: e.target.value })}>
            <option value="">All</option>
            {MODULES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <label className="ma-select">Outcome
          <select value={filters.outcome} onChange={(e) => set({ outcome: e.target.value as any })}>
            <option value="">All</option><option value="ok">Success</option><option value="client">Client errors</option>
            <option value="server">Server errors</option><option value="limited">Rate limited</option><option value="auth">Auth failures (401/403)</option>
          </select>
        </label>
        <form className="ma-search" onSubmit={(e) => { e.preventDefault(); set({ q: query }); }}>
          <Search size={14} />
          <input placeholder="Path or correlation / request id" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search log" />
        </form>
        <span className="ma-spacer" />
        <button className="ma-btn" onClick={() => load(false, null)} aria-label="Refresh"><RefreshCw size={14} /> Refresh</button>
      </div>
      {error && <div className="ma-error">{error}</div>}
      <div className="ma-card">
        <div className="ma-tablewrap">
          <table className="ma-table">
            <thead>
              <tr>
                <th>Time</th>{partnerId === undefined && <th>Partner</th>}<th>Dir</th><th>Method</th><th>Path</th>
                <th>Module</th><th className="num">HTTP</th><th className="num">OCPI</th><th className="num">ms</th><th>Key</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan={partnerId === undefined ? 10 : 9} className="ma-empty">No requests in this window.</td></tr>
              )}
              {rows.map((r) => {
                const cls = statusClass(r.http_status, r.ocpi_status, r.rate_limited);
                return (
                  <tr key={r.id} className="clickable" onClick={() => openRow(r.id)}>
                    <td title={r.created_at}>{fmtTs(r.created_at)}</td>
                    {partnerId === undefined && <td>{r.partner_name || <span className="sub">unauthenticated</span>}</td>}
                    <td><span className={`ma-pill ${r.direction === "IN" ? "info" : "muted"}`}>{r.direction}</span></td>
                    <td>{r.method}</td>
                    <td title={r.url} style={{ maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis" }}>{r.url}</td>
                    <td>{r.module || "—"}</td>
                    <td className={`num ocpi-status-code ${cls}`}>{r.http_status ?? "—"}{r.rate_limited ? " ⏱" : ""}</td>
                    <td className={`num ocpi-status-code ${cls}`}>{r.ocpi_status ?? "—"}</td>
                    <td className="num">{r.duration_ms ?? "—"}</td>
                    <td className="sub">{r.token_kind ? `${r.token_kind} …${r.token_hint}` : "—"}</td>
                  </tr>
                );
              })}
              {loading && <tr><td colSpan={10} className="ma-loading">Loading…</td></tr>}
            </tbody>
          </table>
        </div>
        {next && (
          <div className="ma-pagination">
            <span>{rows.length} shown</span>
            <button onClick={() => load(true, next)} disabled={loading}>Load older</button>
          </div>
        )}
      </div>

      {open && (
        <div className="ma-drawer-bg" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(null); }}>
          <div className="ma-drawer" role="dialog" aria-modal="true" aria-label="Request detail">
            <div className="ma-drawer-h">
              <h3>{open.method} {open.module || ""} · {open.direction === "IN" ? "inbound" : "outbound"}</h3>
              <button onClick={() => setOpen(null)} aria-label="Close"><X size={18} /></button>
            </div>
            <div className="ma-drawer-b">
              <dl className="ocpi-kv">
                <dt>Time</dt><dd>{fmtTs(open.created_at)}</dd>
                <dt>Partner</dt><dd>{open.partner_name || "unauthenticated"}</dd>
                <dt>URL</dt><dd>{open.url}</dd>
                <dt>HTTP / OCPI</dt><dd>{open.http_status ?? "—"} / {open.ocpi_status ?? "—"}{open.rate_limited ? " (rate limited)" : ""}</dd>
                <dt>Duration</dt><dd>{open.duration_ms ?? "—"} ms</dd>
                <dt>Request ID</dt><dd>{open.request_id || "—"}</dd>
                <dt>Correlation ID</dt><dd>{open.correlation_id || "—"} {open.correlation_id && <CopyBtn value={open.correlation_id} />}</dd>
                <dt>From / to party</dt><dd>{open.from_party || "—"} → {open.to_party || "—"}</dd>
                <dt>Client IP</dt><dd>{open.client_ip || "—"}</dd>
                <dt>Key used</dt><dd>{open.token_kind ? `${open.token_kind === "A" ? "registration" : "working"} key …${open.token_hint}` : "—"}</dd>
                {open.error && (<><dt>Error</dt><dd>{open.error}</dd></>)}
              </dl>
              <h4 style={{ margin: "16px 0 6px" }}>Headers (redacted)</h4>
              <pre className="ocpi-json">{JSON.stringify(open.request_headers || {}, null, 2)}</pre>
              <h4 style={{ margin: "16px 0 6px" }}>Request body</h4>
              <pre className="ocpi-json">{open.request_body === null && open.request_bytes ? "(bodies not logged for this partner)" : pretty(open.request_body)}</pre>
              <h4 style={{ margin: "16px 0 6px" }}>Response body</h4>
              <pre className="ocpi-json">{open.response_body === null && open.response_bytes ? "(bodies not logged for this partner)" : pretty(open.response_body)}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OcpiLogViewer;
